/**
 * 报名详情「表单填写内容」的**纯逻辑**
 * -----------------------------------------------------------------------------
 * 与 `RegistrationDetail.tsx` 分开是为了可单测：那个文件 import 了 `layout`，
 * 在 vitest 的 node 环境里跑不起来。页面只负责画，怎么拼行全在这里。
 *
 * 三条口径（都不是可选项）：
 *  1. **已填的一个都不能少** —— 按 Schema 字段全量出列，未填的写「未填写」，
 *     而不是「没填就不出现」。后者在学员眼里就是「我的内容丢了」。
 *  2. **不印机器值** —— 显示的是字段 `label` 与选项 `label`，不是 `key` / `middle` /
 *     `[object Object]`。版本不一致时也用**最新已发布 Schema** 的 label 翻译一遍。
 *  3. **键还在就要显示** —— 最新 Schema 删掉的字段（老报名单里还有值）追加在最后，
 *     label 退回键名，绝不因为版本升级把内容吞掉。
 */
import {
  FieldType,
  type FormFieldDef,
  type FormSchemaView,
  formatRegionText,
} from '@enroll/shared';
import { formatAnswerText } from '../components/EnrollConfirm.logic';
import { maskPhone } from '../lib/phone-format';

/** 值缺失时的占位词（与确认页同一口径，学员要看出**漏了什么**） */
export const EMPTY_TEXT = '未填写';

/** 最新 Schema 已删除的字段（老单子里还有值）归到这张卡，避免半行无标题 */
const UNKNOWN_GROUP = '__other__';

export interface AnswerRow {
  key: string;
  label: string;
  value: string;
  /** 签名行：有短期链接就画图，没有就是 `null`（退回文案） */
  imageUrl: string | null;
}

export interface AnswerSection {
  key: string;
  /** 分组名（取自 Schema `groups`）；空串表示「不是按分组排的」 */
  title: string;
  rows: AnswerRow[];
}

/**
 * 没有字段定义时把值本身渲染成文案。
 *
 * 只有版本漂移、且该键已被新 Schema 删掉时才会走到这里 —— 此时唯一能保证的是
 * 「原样呈现」，绝不能返回 `[object Object]`。
 */
function formatLooseValue(value: unknown): string {
  if (value === undefined || value === null || value === '') return EMPTY_TEXT;
  if (typeof value === 'boolean') return value ? '是' : '否';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value)) {
    const text = value
      .map((item) => formatLooseValue(item))
      .filter((item) => item !== EMPTY_TEXT)
      .join('、');
    return text || EMPTY_TEXT;
  }
  // 只剩 `region` 那种对象：码表解析得出就拼成「省 / 市 / 区 详细」
  return formatRegionText(value) || EMPTY_TEXT;
}

/**
 * 字段值 → 只读文案。
 *
 * 复用确认页的 `formatAnswerText`（region / 上传 / 签名 / 选项都在里面），只在它
 * 认不出来时兜一层：`region` 的历史值是整串文本，码表解析不出来会得到空串，
 * 这时按原文显示 —— 不能因为「格式化失败」把内容显示成「未填写」。
 */
function textOf(field: FormFieldDef | undefined, value: unknown): string {
  if (!field) return formatLooseValue(value);
  if (field.type === FieldType.REGION) {
    const region = formatRegionText(value);
    if (region) return region;
    return formatLooseValue(value);
  }
  return formatAnswerText(field, value);
}

/**
 * 拼出详情页要显示的卡片与行。
 *
 * `hiddenKeys` 是页面上方「报名信息」卡已经展示过的系统字段（姓名 / 手机号 / 证件号）——
 * 同一页里同一份数据出现两遍只是噪音。由调用方决定要不要传：服务端没下发
 * `applicantName` 时就不该隐藏同名表单字段，否则是真的丢内容。
 */
export function buildAnswerSections(input: {
  formData: Record<string, unknown>;
  signatureUrls?: Record<string, string>;
  schema?: FormSchemaView;
  /** `published.version === registration.schemaVersion` */
  versionMatched: boolean;
  hiddenKeys?: readonly string[];
}): AnswerSection[] {
  const { formData, signatureUrls = {}, schema, versionMatched, hiddenKeys = [] } = input;
  const hidden = new Set(hiddenKeys);
  const rowOf = (key: string, field: FormFieldDef | undefined): AnswerRow => {
    // 「签的是什么」看这张表，不看字段类型 —— 版本漂移时字段定义可能已经没了，
    // 但链接是按 `field.key` 下发的，仍然画得出签名图。
    const imageUrl = signatureUrls[key] ?? null;
    return {
      key,
      label: field?.label ?? key,
      // 有链接就说明这是签名行：值固定写「已签名」，任何情况下都不把 assetId 上屏
      value: imageUrl ? '已签名' : textOf(field, formData[key]),
      imageUrl,
    };
  };

  const sections = new Map<string, AnswerSection>();
  const push = (groupKey: string, title: string, row: AnswerRow): void => {
    const section = sections.get(groupKey) ?? { key: groupKey, title, rows: [] };
    section.rows.push(row);
    sections.set(groupKey, section);
  };

  const groups = [...(schema?.groups ?? [])].sort((a, b) => a.order - b.order);
  const groupRank = new Map(groups.map((group, index) => [group.key, index]));
  const groupTitle = new Map(groups.map((group) => [group.key, group.label]));
  const knownByKey = new Map((schema?.fields ?? []).map((field) => [field.key, field]));

  /*
    ⚠️ `order` 是**组内序号**（seed 里 basic 是 1~5、education 是 1~15、extra 是 1~4），
    跨组直接比 `order` 会把三组揉在一起（民族 1 / 手机号 2 / 文化程度 2 交错出现）。
    必须先比组序号、再比组内序号。这个坑在渲染层看不出来，只有按组排的时候才暴露。
  */
  const byRank = (a: FormFieldDef, b: FormFieldDef): number => {
    const groupDiff =
      (groupRank.get(a.group) ?? groups.length) - (groupRank.get(b.group) ?? groups.length);
    return groupDiff !== 0 ? groupDiff : a.order - b.order;
  };

  if (schema && versionMatched) {
    for (const field of [...schema.fields].sort(byRank)) {
      if (hidden.has(field.key)) continue;
      // 字段指向一个没在 `groups` 里声明的分组：仍要显示，标题退回「表单填写内容」
      push(field.group, groupTitle.get(field.group) ?? '', rowOf(field.key, field));
    }
    return [...sections.values()].filter((section) => section.rows.length > 0);
  }

  /*
    版本不一致（或 Schema 根本没取到）：不能拿新版本字段硬套（新表单上有、老单子里
    根本没有的字段，补一行「未填写」是凭空捏造），只列 `formData` 里真有的键；
    但字段名与选项文案借用**最新版本**翻译，绝不退化成「英文键: 原始枚举值」。
  */
  for (const field of [...knownByKey.values()]
    .filter((item) => item.key in formData && !hidden.has(item.key))
    .sort(byRank)) {
    push(field.group, groupTitle.get(field.group) ?? '', rowOf(field.key, field));
  }
  // 最新 Schema 已删除、但老单子里还有值的键：无组可归，单独一张卡，值原样呈现
  for (const key of Object.keys(formData)) {
    if (knownByKey.has(key) || hidden.has(key)) continue;
    push(UNKNOWN_GROUP, '其他信息', rowOf(key, undefined));
  }
  return [...sections.values()].filter((section) => section.rows.length > 0);
}

/** 收件手机号脱敏：与页面顶部「手机号」行（服务端 `phoneMasked`）同一观感 */
export function maskAddressPhone(phone: string): string {
  // 规则只有一处（`lib/phone-format`），这里保留函数名是为了不动调用方与用例
  return maskPhone(phone);
}
